/**
 * Detects whether a string looks like an XML document.
 *
 * @param {string} str - input string
 * @returns {boolean} does the string look like xml?
 */
export function looksLikeXml(str) {
    if (typeof str !== "string") {
        return false;
    }

    const trimmed = str.trim();
    return trimmed.startsWith("<") && trimmed.endsWith(">");
}

/**
 * Defines an own enumerable property without invoking inherited setters.
 *
 * @param {Object} obj - Object to update.
 * @param {string} key - Property name.
 * @param {*} value - Property value.
 * @returns {void}
 */
export function xmlSetProperty(obj, key, value) {
    Object.defineProperty(obj, key, {
        value,
        writable: true,
        enumerable: true,
        configurable: true,
    });
}

/**
 * Adds a child value, converting repeated element names to an array.
 *
 * @param {Object} node - Parent JSON node.
 * @param {string} key - Child element name.
 * @param {*} value - Converted child value.
 * @returns {void}
 */
export function xmlAddChild(node, key, value) {
    if (!Object.hasOwn(node, key)) {
        xmlSetProperty(node, key, value);
        return;
    }

    const existing = node[key];
    if (Array.isArray(existing)) {
        existing.push(value);
    } else {
        xmlSetProperty(node, key, [existing, value]);
    }
}

/**
 * Converts an XML element and its descendants to the REST JSON shape.
 *
 * Attributes use `@name`; mixed-content text uses `#text`; repeated child
 * elements become arrays. Returns `null` when the nesting limit is exceeded.
 *
 * @param {Element} element - Element to convert.
 * @param {number} depth - Current nesting depth.
 * @returns {Object|string|null} Converted element value.
 */
export function xmlElementToJson(element, depth = 0) {
    if (depth > 200) {
        return null;
    }

    const node = {};
    for (const attr of element.attributes) {
        xmlSetProperty(node, `@${attr.name}`, attr.value);
    }
    const hasAttrs = element.attributes.length > 0;

    let hasChildren = false;
    let text = "";
    for (const child of element.childNodes) {
        // ELEMENT_NODE
        if (child.nodeType === 1) {
            const value = xmlElementToJson(child, depth + 1);
            if (value !== null) {
                hasChildren = true;
                xmlAddChild(node, child.tagName, value);
            }

            continue;
        }

        // TEXT_NODE or CDATA_SECTION_NODE
        if (child.nodeType === 3 || child.nodeType === 4) {
            text += child.data;
        }
    }
    text = text.trim();

    if (!hasChildren && !hasAttrs) {
        return text;
    }

    if (text) {
        node["#text"] = text;
    }

    return node;
}

/**
 * Converts a well-formed XML document to JSON.
 *
 * @param {string} xmlString - XML source value.
 * @returns {Record<string, *>} Converted root element, or `{}` for invalid XML.
 */
export function xmlToJson(xmlString) {
    const result = {};

    /** @type {Document|null} */
    let doc = null;
    try {
        doc = new DOMParser().parseFromString(String(xmlString), "application/xml");
    } catch {
        return result;
    }

    const root = doc?.documentElement;
    if (!root || doc.querySelectorAll("parsererror").length > 0) {
        return result;
    }

    xmlSetProperty(result, root.tagName, xmlElementToJson(root));
    return result;
}
